/**
 * DraftReviewPage
 * ===============
 * Bulk review page for ALL pending KB drafts. Shows field-level diffs for
 * UPDATE drafts, supports individual and bulk approve/reject actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { ConfirmationModal } from '../components/ui/Modal';
import { useToast, useOrg } from '../hooks';
import { getPendingDrafts, getDraft, approveDraft, rejectDraft, bulkRejectDrafts } from '../api/brain';

const DRAFT_TYPE_CONFIG = {
  create: { label: 'Add', color: 'bg-green-100 text-green-700' },
  update: { label: 'Update', color: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Remove', color: 'bg-red-100 text-red-700' },
};

const SOURCE_LABELS = {
  chat: 'Brain Chat',
  proposal_feedback: 'Proposal Feedback',
  meeting_transcript: 'Meeting Transcript',
};

/* ─── Field-level diff helpers ────────────────────────────────────────────── */

const formatValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const computeFieldDiff = (currentData, proposedData) => {
  const current = currentData && typeof currentData === 'object' ? currentData : {};
  const proposed = proposedData && typeof proposedData === 'object' ? proposedData : {};
  const allKeys = [...new Set([...Object.keys(current), ...Object.keys(proposed)])].sort();

  return allKeys.map(key => {
    const inCurrent = key in current;
    const inProposed = key in proposed;
    const currentVal = current[key];
    const proposedVal = proposed[key];

    if (!inCurrent && inProposed) {
      return { key, type: 'added', current: null, proposed: proposedVal };
    }
    if (inCurrent && !inProposed) {
      return { key, type: 'removed', current: currentVal, proposed: null };
    }
    const changed = JSON.stringify(currentVal) !== JSON.stringify(proposedVal);
    return { key, type: changed ? 'changed' : 'unchanged', current: currentVal, proposed: proposedVal };
  });
};

const DIFF_ROW_STYLES = {
  added: 'bg-green-50',
  removed: 'bg-red-50',
  changed: 'bg-amber-50',
  unchanged: '',
};

/* ─── Render helpers ──────────────────────────────────────────────────────── */

const renderDataFields = (data) => {
  if (!data || typeof data !== 'object') return <span className="text-sm text-gray-400">No data</span>;
  const entries = Object.entries(data);
  if (entries.length === 0) return <span className="text-sm text-gray-400">Empty</span>;
  return (
    <div className="space-y-1.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-start gap-3 py-1 border-b border-gray-100 last:border-0">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[120px] shrink-0 pt-0.5">
            {key.replace(/_/g, ' ')}
          </span>
          <span className="text-sm text-gray-900 break-words">
            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ─── Draft Card component ────────────────────────────────────────────────── */

/* ─── System prompt diff renderer ────────────────────────────────────────── */

const SystemPromptDiff = ({ draft }) => {
  const currentPrompt = draft.current_prompt;
  const proposedChanges = draft.data || {};

  return (
    <div className="space-y-4">
      {/* Prompt metadata */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Prompt:</span>
        <span className="text-sm font-medium text-gray-900">{draft.target_prompt_name || draft.name}</span>
        {currentPrompt?.usage_context && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {currentPrompt.usage_context}
          </span>
        )}
        {currentPrompt?.version_number && (
          <span className="text-xs text-gray-400">v{currentPrompt.version_number}</span>
        )}
      </div>

      {/* Prompt template diff */}
      {proposedChanges.prompt_template && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prompt Template</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-red-600 mb-1">Current</p>
              <pre className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-gray-800 whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
                {currentPrompt?.prompt_template || '(not available)'}
              </pre>
            </div>
            <div>
              <p className="text-xs font-medium text-green-600 mb-1">Proposed</p>
              <pre className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-gray-800 whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
                {proposedChanges.prompt_template}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Other changed fields */}
      {Object.entries(proposedChanges)
        .filter(([key]) => key !== 'prompt_template')
        .map(([key, value]) => (
          <div key={key} className="flex items-start gap-3 py-1 border-b border-gray-100 last:border-0">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[120px] shrink-0 pt-0.5">
              {key.replace(/_/g, ' ')}
            </span>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div className="text-sm text-red-600">
                {currentPrompt?.[key] !== undefined ? formatValue(currentPrompt[key]) : '(not set)'}
              </div>
              <div className="text-sm text-green-700 font-medium">
                {formatValue(value)}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

const InternalKnowledgeDiff = ({ draft }) => {
  const proposedData = draft.data || {};
  const decisionPoints = proposedData.decision_points || [];
  const confidence = proposedData.confidence || 'medium';
  const workerTags = proposedData.worker_tags || [];

  const confidenceColors = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4">
      {/* Metadata */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">Confidence:</span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceColors[confidence] || 'bg-gray-100 text-gray-600'}`}>
            {confidence}
          </span>
        </div>
        {workerTags.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workers:</span>
            {workerTags.map(tag => (
              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                {tag.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
        {proposedData.source_proposal_version && (
          <span className="text-xs text-gray-400">from proposal v{proposedData.source_proposal_version}</span>
        )}
      </div>

      {/* Decision Points */}
      {decisionPoints.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Decision Points ({decisionPoints.length})
          </p>
          <div className="space-y-2">
            {decisionPoints.map((dp, i) => (
              <div key={i} className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900 mb-1">{dp.point || 'N/A'}</p>
                {dp.context && <p className="text-xs text-gray-600 mb-0.5"><span className="font-semibold">Context:</span> {dp.context}</p>}
                {dp.rationale && <p className="text-xs text-gray-600 mb-0.5"><span className="font-semibold">Rationale:</span> {dp.rationale}</p>}
                {dp.section_source && <p className="text-xs text-gray-400"><span className="font-semibold">Source:</span> {dp.section_source}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comparison with current IK entry if updating */}
      {draft.current_ik_entry && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Current Entry</p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-900 mb-1">{draft.current_ik_entry.title}</p>
            <div className="flex gap-2 flex-wrap mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceColors[draft.current_ik_entry.confidence] || 'bg-gray-100 text-gray-600'}`}>
                {draft.current_ik_entry.confidence}
              </span>
              {(draft.current_ik_entry.worker_tags || []).map(tag => (
                <span key={tag} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                  {tag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
            {(draft.current_ik_entry.decision_points || []).map((dp, i) => (
              <p key={i} className="text-xs text-red-700 mt-1">- {dp.point}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DraftCard = ({ draft, onApprove, onReject, submittingId }) => {
  const typeInfo = DRAFT_TYPE_CONFIG[draft.draft_type] || { label: draft.draft_type, color: 'bg-gray-100 text-gray-700' };
  const isSubmitting = submittingId === draft.id;
  const isSystemPrompt = draft.target_type === 'system_prompt';
  const isInternalKnowledge = draft.target_type === 'internal_knowledge';
  const isKBEntry = !isSystemPrompt && !isInternalKnowledge;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Card header */}
      <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
          {isKBEntry && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
              Structured Entity
            </span>
          )}
          {isSystemPrompt && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
              System Prompt
            </span>
          )}
          {isInternalKnowledge && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">
              Internal Knowledge
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">
            {draft.category}
          </span>
          <span className="text-sm font-medium text-gray-900 truncate">{draft.name}</span>
          {draft.source && (
            <span className="text-xs text-gray-400">
              {SOURCE_LABELS[draft.source] || draft.source}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-shrink-0">
          <button
            onClick={() => onReject(draft.id)}
            disabled={!!submittingId}
            className="flex-1 sm:flex-none px-3.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-40 transition-colors"
          >
            {isSubmitting ? '...' : 'Reject'}
          </button>
          <button
            onClick={() => onApprove(draft.id)}
            disabled={!!submittingId}
            className="flex-1 sm:flex-none px-3.5 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors shadow-sm"
          >
            {isSubmitting ? '...' : 'Approve'}
          </button>
        </div>
      </div>

      {/* Card body — content varies by draft type and target type */}
      <div className="px-5 py-4">
        {isInternalKnowledge ? (
          <InternalKnowledgeDiff draft={draft} />
        ) : isSystemPrompt ? (
          <SystemPromptDiff draft={draft} />
        ) : draft.draft_type === 'update' && draft.current_entry ? (
          /* Field-level diff table */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="text-left py-2 pr-4 w-1/5">Field</th>
                  <th className="text-left py-2 pr-4 w-2/5">Current</th>
                  <th className="text-left py-2 w-2/5">Proposed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {computeFieldDiff(draft.current_entry.data, draft.data).map(row => (
                  <tr key={row.key} className={DIFF_ROW_STYLES[row.type]}>
                    <td className="py-2 pr-4 font-medium text-gray-600 align-top">
                      {row.key.replace(/_/g, ' ')}
                    </td>
                    <td className="py-2 pr-4 text-gray-700 align-top whitespace-pre-wrap break-words">
                      {row.type === 'added' ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <span className={row.type === 'removed' ? 'line-through text-red-600' : row.type === 'changed' ? 'text-red-600' : ''}>
                          {formatValue(row.current)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-gray-700 align-top whitespace-pre-wrap break-words">
                      {row.type === 'removed' ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <span className={row.type === 'added' ? 'text-green-700 font-medium' : row.type === 'changed' ? 'text-green-700 font-medium' : ''}>
                          {formatValue(row.proposed)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : draft.draft_type === 'update' && !draft.current_entry ? (
          /* Update but current entry missing — show proposed data */
          <div>
            <p className="text-xs text-gray-500 italic mb-3">Current entry not found — showing proposed data only.</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              {renderDataFields(draft.data)}
            </div>
          </div>
        ) : draft.draft_type === 'delete' ? (
          /* Delete — show entry being removed */
          <div>
            {draft.current_entry ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                {renderDataFields(draft.current_entry.data)}
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-gray-500 italic mb-2">
                  Target entry not found — it may have already been removed.
                </p>
                {draft.data && Object.keys(draft.data).length > 0 && renderDataFields(draft.data)}
              </div>
            )}
          </div>
        ) : (
          /* Create — show new entry data */
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            {renderDataFields(draft.data)}
          </div>
        )}

        {/* AI Rationale */}
        {draft.ai_rationale && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">AI Rationale</p>
            <p className="text-sm text-blue-900 leading-relaxed">{draft.ai_rationale}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main page component ─────────────────────────────────────────────────── */

const DraftReviewPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { activeOrg } = useOrg();

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [bulkAction, setBulkAction] = useState(null); // { type: 'approve'|'reject', done: 0, total: 0 }
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'approve'|'reject'|'bulk-approve'|'bulk-reject', draftId?, draftName? }

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      // The drafts endpoint caps each response at `limit`, so page through until
      // we've collected every pending draft. Otherwise the review list would be
      // capped (e.g. 50) while the banner shows the true `total` (e.g. 130).
      const PAGE_SIZE = 100;
      const pendingList = [];
      let total = Infinity;
      while (pendingList.length < total) {
        const listRes = await getPendingDrafts('pending', null, PAGE_SIZE, pendingList.length);
        const page = listRes.data?.drafts || [];
        total = listRes.data?.total ?? pendingList.length + page.length;
        pendingList.push(...page);
        if (page.length === 0) break; // safety against an unexpected total
      }

      // Fetch enriched data (with current_entry) for each draft. Run in bounded
      // batches rather than all at once — with 100+ pending drafts an unbounded
      // Promise.all would fire 100+ concurrent detail requests (each doing a
      // requester lookup server-side) and spike the backend.
      const enrichDraft = async (d) => {
        try {
          const detail = await getDraft(d.id);
          return detail.data;
        } catch {
          // Fall back to list data if detail fetch fails
          return d;
        }
      };

      const ENRICH_CONCURRENCY = 15;
      const enriched = [];
      for (let i = 0; i < pendingList.length; i += ENRICH_CONCURRENCY) {
        const batch = pendingList.slice(i, i + ENRICH_CONCURRENCY);
        enriched.push(...(await Promise.all(batch.map(enrichDraft))));
      }

      setDrafts(enriched);
    } catch {
      showToast('Failed to load pending drafts', 'error');
    } finally {
      setLoading(false);
    }
  // Refetch when the active org changes so the queue reflects the current org
  }, [showToast, activeOrg?.id]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const requestApprove = (draftId) => {
    const draft = drafts.find(d => d.id === draftId);
    setConfirmAction({ type: 'approve', draftId, draftName: draft?.name || 'this draft' });
  };

  const requestReject = (draftId) => {
    const draft = drafts.find(d => d.id === draftId);
    setConfirmAction({ type: 'reject', draftId, draftName: draft?.name || 'this draft' });
  };

  const requestBulkAction = (type) => {
    setConfirmAction({ type: `bulk-${type}` });
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) return;

    if (action.type === 'approve') {
      await handleApprove(action.draftId);
    } else if (action.type === 'reject') {
      await handleReject(action.draftId);
    } else if (action.type === 'bulk-approve') {
      await handleBulkAction('approve');
    } else if (action.type === 'bulk-reject') {
      await handleBulkAction('reject');
    }
  };

  const getConfirmationProps = () => {
    if (!confirmAction) return {};
    switch (confirmAction.type) {
      case 'approve':
        return {
          title: 'Approve Draft',
          message: `Are you sure you want to approve "${confirmAction.draftName}"? This will apply the proposed changes.`,
          confirmText: 'Approve',
          confirmVariant: 'primary',
        };
      case 'reject':
        return {
          title: 'Reject Draft',
          message: `Are you sure you want to reject "${confirmAction.draftName}"? This action cannot be undone.`,
          confirmText: 'Reject',
          confirmVariant: 'danger',
        };
      case 'bulk-approve':
        return {
          title: 'Approve All Drafts',
          message: `Are you sure you want to approve all ${drafts.length} pending draft${drafts.length !== 1 ? 's' : ''}? This will apply all proposed changes.`,
          confirmText: 'Approve All',
          confirmVariant: 'primary',
        };
      case 'bulk-reject':
        return {
          title: 'Reject All Drafts',
          message: `Are you sure you want to reject all ${drafts.length} pending draft${drafts.length !== 1 ? 's' : ''}? This action cannot be undone.`,
          confirmText: 'Reject All',
          confirmVariant: 'danger',
        };
      default:
        return {};
    }
  };

  const handleApprove = async (draftId) => {
    setSubmittingId(draftId);
    try {
      await approveDraft(draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      showToast('Draft approved and applied', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to approve', 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleReject = async (draftId) => {
    setSubmittingId(draftId);
    try {
      await rejectDraft(draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      showToast('Draft rejected', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to reject', 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleBulkAction = async (type) => {
    const remaining = [...drafts];
    const total = remaining.length;
    setBulkAction({ type, done: 0, total });

    if (type === 'reject') {
      // Use bulk reject endpoint — single request instead of N sequential calls
      try {
        const draftIds = remaining.map((d) => d.id);
        await bulkRejectDrafts(draftIds);
        setBulkAction(null);
        showToast(`All ${total} drafts rejected`, 'success');
        navigate('/brain');
      } catch {
        setBulkAction(null);
        showToast('Failed to reject drafts', 'error');
        fetchDrafts();
      }
      return;
    }

    // Approve still uses sequential calls (each has side effects)
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < remaining.length; i++) {
      try {
        await approveDraft(remaining[i].id);
        succeeded++;
      } catch {
        failed++;
      }
      setBulkAction({ type, done: i + 1, total });
    }

    setBulkAction(null);

    if (failed > 0) {
      showToast(`${succeeded} ${type}d, ${failed} failed`, failed === total ? 'error' : 'warning');
      fetchDrafts();
    } else {
      showToast(`All ${total} drafts ${type}d`, 'success');
      navigate('/brain');
    }
  };

  const isBusy = !!submittingId || !!bulkAction;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Back navigation */}
        <button
          onClick={() => navigate('/brain')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary mb-6 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Brain
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : drafts.length === 0 ? (
          /* Empty state */
          <div className="text-center py-24">
            <svg className="w-16 h-16 text-green-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">All caught up!</h2>
            <p className="text-gray-500 mb-6">No pending drafts to review.</p>
            <button
              onClick={() => navigate('/brain')}
              className="text-primary hover:text-primary-600 text-sm font-medium"
            >
              Return to Brain
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-sans font-semibold text-gray-900 dark:text-white">
                  Review Pending Updates
                </h1>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                  {drafts.length}
                </span>
              </div>
            </div>

            {/* Sticky bulk action bar */}
            <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm border border-gray-200 rounded-xl px-5 py-3 mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
              {bulkAction ? (
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span className="text-sm font-medium text-gray-700">
                    {bulkAction.type === 'approve' ? 'Approving' : 'Rejecting'} {bulkAction.done}/{bulkAction.total}...
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {drafts.length} draft{drafts.length !== 1 ? 's' : ''} pending review
                </p>
              )}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => requestBulkAction('reject')}
                  disabled={isBusy}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-40 transition-colors"
                >
                  Reject All ({drafts.length})
                </button>
                <button
                  onClick={() => requestBulkAction('approve')}
                  disabled={isBusy}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors shadow-sm"
                >
                  Approve All ({drafts.length})
                </button>
              </div>
            </div>

            {/* Draft cards */}
            <div className="space-y-4">
              {drafts.map(draft => (
                <DraftCard
                  key={draft.id}
                  draft={draft}
                  onApprove={requestApprove}
                  onReject={requestReject}
                  submittingId={submittingId}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        {...getConfirmationProps()}
      />
    </DashboardLayout>
  );
};

export default DraftReviewPage;
